import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Radio,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  LockOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface PlatformAccount {
  id: number;
  platform: string;
  accountName: string;
  accountId: string;
  username: string;
  password?: string;
  accessToken?: string;
  refreshToken?: string;
  apiKey?: string;
  apiSecret?: string;
  status: 'active' | 'inactive' | 'banned';
  publishStrategy: 'auto' | 'manual' | 'scheduled';
  publishSchedule?: string;
  dailyPublishLimit: number;
  currentPublishCount: number;
  lastPublishTime?: string;
  dailyPrivateChatCount: number; // 每日主动私聊用户人数
  todayPrivateChatCount: number; // 今日已私聊人数
  lastPrivateChatTime?: string; // 最后私聊时间
  todayPublishCount: number; // 今日已发布数量
  todayCommentCount: number; // 今日已评论数量
  persona: string; // 账号人设，如：纯评论、美女主播、体育专业分析
  description?: string;
  createTime: string;
  updateTime: string;
}

const PLATFORMS = [
  'Facebook',
  'TikTok',
  'YouTube',
  'Instagram',
  'Shopee',
  'Zalo',
  'Lazada',
  'Bigo Live',
  'Tiki',
  'TikTok Shop'
];

// Demo数据
const generateAccounts = (platform: string): PlatformAccount[] => {
  return [
    {
      id: Date.now() + Math.random(),
      platform,
      accountName: `${platform}账号1`,
      accountId: `${platform.toLowerCase()}_001`,
      username: `${platform.toLowerCase()}_user1`,
      password: '******',
      accessToken: 'token_xxx',
      status: 'active',
      publishStrategy: 'auto',
      publishSchedule: '09:00,14:00,20:00',
      dailyPublishLimit: 10,
      currentPublishCount: 3,
      lastPublishTime: '2025-12-04 09:00:00',
      dailyPrivateChatCount: 50,
      todayPrivateChatCount: 12,
      lastPrivateChatTime: '2025-12-04 10:30:00',
      todayPublishCount: Math.floor(Math.random() * 10) + 1,
      todayCommentCount: Math.floor(Math.random() * 20) + 5,
      persona: platform === 'TikTok' ? '美女主播' : platform === 'YouTube' ? '体育专业分析' : '纯评论',
      description: `${platform}主账号`,
      createTime: '2025-12-01 10:00:00',
      updateTime: '2025-12-04 09:00:00',
    },
    {
      id: Date.now() + Math.random() + 1,
      platform,
      accountName: `${platform}账号2`,
      accountId: `${platform.toLowerCase()}_002`,
      username: `${platform.toLowerCase()}_user2`,
      password: '******',
      accessToken: 'token_yyy',
      status: 'active',
      publishStrategy: 'scheduled',
      publishSchedule: '10:00,15:00,19:00',
      dailyPublishLimit: 5,
      currentPublishCount: 2,
      lastPublishTime: '2025-12-04 10:00:00',
      dailyPrivateChatCount: 30,
      todayPrivateChatCount: 8,
      lastPrivateChatTime: '2025-12-04 11:00:00',
      todayPublishCount: Math.floor(Math.random() * 5) + 1,
      todayCommentCount: Math.floor(Math.random() * 15) + 3,
      persona: platform === 'Bigo Live' ? '美女主播' : platform === 'Facebook' ? '体育专业分析' : '纯评论',
      description: `${platform}备用账号`,
      createTime: '2025-12-01 10:00:00',
      updateTime: '2025-12-04 10:00:00',
    },
  ];
};

const PlatformAccountConfig: React.FC = () => {
  const [accounts, setAccounts] = useState<Record<string, PlatformAccount[]>>(() => {
    const data: Record<string, PlatformAccount[]> = {};
    PLATFORMS.forEach(platform => {
      data[platform] = generateAccounts(platform);
    });
    return data;
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PlatformAccount | null>(null);
  const [currentPlatform, setCurrentPlatform] = useState<string>(PLATFORMS[0]);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ platform: currentPlatform });
    setModalVisible(true);
  };

  const handleEdit = (record: PlatformAccount) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      password: undefined, // 不显示密码
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number, platform: string) => {
    setAccounts(prev => ({
      ...prev,
      [platform]: prev[platform].filter(item => item.id !== id),
    }));
    message.success('删除成功');
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      const platform = values.platform || currentPlatform;
      if (editingRecord) {
        setAccounts(prev => ({
          ...prev,
          [platform]: prev[platform].map(item =>
            item.id === editingRecord.id
              ? { ...item, ...values, updateTime: new Date().toLocaleString('zh-CN') }
              : item
          ),
        }));
        message.success('更新成功');
      } else {
        const newRecord: PlatformAccount = {
          id: Date.now(),
          ...values,
          platform,
          status: 'active',
          currentPublishCount: 0,
          dailyPrivateChatCount: 50,
          todayPrivateChatCount: 0,
          todayPublishCount: 0,
          todayCommentCount: 0,
          persona: '纯评论',
          createTime: new Date().toLocaleString('zh-CN'),
          updateTime: new Date().toLocaleString('zh-CN'),
        };
        setAccounts(prev => ({
          ...prev,
          [platform]: [...(prev[platform] || []), newRecord],
        }));
        message.success('新增成功');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const handleToggleStatus = (id: number, platform: string, status: string) => {
    const newStatus = status === 'active' ? 'inactive' : 'active';
    setAccounts(prev => ({
      ...prev,
      [platform]: prev[platform].map(item =>
        item.id === id ? { ...item, status: newStatus, updateTime: new Date().toLocaleString('zh-CN') } : item
      ),
    }));
    message.success(newStatus === 'active' ? '已启用' : '已禁用');
  };

  const getStatusTag = (status: string) => {
    const map: Record<string, { text: string; color: string }> = {
      active: { text: '启用', color: 'success' },
      inactive: { text: '禁用', color: 'default' },
      banned: { text: '封禁', color: 'error' },
    };
    const info = map[status] || { text: status, color: 'default' };
    return <Tag color={info.color}>{info.text}</Tag>;
  };

  const getStrategyTag = (strategy: string) => {
    const map: Record<string, string> = {
      auto: '自动发布',
      manual: '手动发布',
      scheduled: '定时发布',
    };
    return map[strategy] || strategy;
  };

  const currentAccounts = accounts[currentPlatform] || [];
  const filteredAccounts = currentAccounts.filter(item =>
    item.accountName.includes(searchText) ||
    item.accountId.includes(searchText) ||
    item.username.includes(searchText)
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '账号名称',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 150,
    },
    {
      title: '账号ID',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 150,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '发布策略',
      dataIndex: 'publishStrategy',
      key: 'publishStrategy',
      width: 120,
      render: (strategy: string) => getStrategyTag(strategy),
    },
    {
      title: '发布计划',
      dataIndex: 'publishSchedule',
      key: 'publishSchedule',
      width: 200,
      ellipsis: true,
    },
    {
      title: '每日发布限制',
      dataIndex: 'dailyPublishLimit',
      key: 'dailyPublishLimit',
      width: 120,
    },
    {
      title: '今日已发布',
      dataIndex: 'todayPublishCount',
      key: 'todayPublishCount',
      width: 120,
      render: (count: number, record: PlatformAccount) => (
        <span style={{ color: count >= record.dailyPublishLimit ? '#ff4d4f' : '#52c41a' }}>
          {count} / {record.dailyPublishLimit}
        </span>
      ),
    },
    {
      title: '今日已评论',
      dataIndex: 'todayCommentCount',
      key: 'todayCommentCount',
      width: 120,
      render: (count: number) => (
        <Tag color="blue">{count}</Tag>
      ),
    },
    {
      title: '最后发布时间',
      dataIndex: 'lastPublishTime',
      key: 'lastPublishTime',
      width: 180,
    },
    {
      title: '每日私聊限制',
      dataIndex: 'dailyPrivateChatCount',
      key: 'dailyPrivateChatCount',
      width: 120,
    },
    {
      title: '今日已私聊',
      dataIndex: 'todayPrivateChatCount',
      key: 'todayPrivateChatCount',
      width: 120,
      render: (count: number, record: PlatformAccount) => (
        <span style={{ color: count >= record.dailyPrivateChatCount ? 'red' : 'inherit' }}>
          {count} / {record.dailyPrivateChatCount}
        </span>
      ),
    },
    {
      title: '最后私聊时间',
      dataIndex: 'lastPrivateChatTime',
      key: 'lastPrivateChatTime',
      width: 180,
    },
    {
      title: '账号人设',
      dataIndex: 'persona',
      key: 'persona',
      width: 150,
      render: (persona: string) => (
        <Tag color="blue">{persona}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record: PlatformAccount) => (
        <Switch
          checked={status === 'active'}
          onChange={() => handleToggleStatus(record.id, record.platform, status)}
          disabled={status === 'banned'}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: PlatformAccount) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id, record.platform)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>平台账号配置</h1>
        <p>配置10个平台的账号信息、发布策略和账号策略。支持统计每日主动私聊用户人数</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索账号名称、ID或用户名"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增账号
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('刷新成功')}>
            刷新
          </Button>
        </Space>

        <Tabs activeKey={currentPlatform} onChange={setCurrentPlatform}>
          {PLATFORMS.map(platform => (
            <TabPane tab={platform} key={platform}>
              <Table
                rowKey="id"
                dataSource={filteredAccounts}
                columns={columns}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1500 }}
              />
            </TabPane>
          ))}
        </Tabs>
      </Card>

      <Modal
        title={editingRecord ? '编辑平台账号' : '新增平台账号'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="platform"
            label="平台"
            rules={[{ required: true, message: '请选择平台' }]}
          >
            <Select disabled={!!editingRecord}>
              {PLATFORMS.map(platform => (
                <Option key={platform} value={platform}>{platform}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="accountName"
            label="账号名称"
            rules={[{ required: true, message: '请输入账号名称' }]}
          >
            <Input placeholder="如：抖音账号1" />
          </Form.Item>
          <Form.Item
            name="accountId"
            label="账号ID"
            rules={[{ required: true, message: '请输入账号ID' }]}
          >
            <Input placeholder="如：douyin_001" />
          </Form.Item>
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item
            name="password"
            label="密码"
            rules={editingRecord ? [] : [{ required: true, message: '请输入密码' }]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>
          <Form.Item name="accessToken" label="Access Token">
            <Input placeholder="请输入Access Token" />
          </Form.Item>
          <Form.Item name="refreshToken" label="Refresh Token">
            <Input placeholder="请输入Refresh Token" />
          </Form.Item>
          <Form.Item name="apiKey" label="API Key">
            <Input placeholder="请输入API Key" />
          </Form.Item>
          <Form.Item name="apiSecret" label="API Secret">
            <Input.Password placeholder="请输入API Secret" />
          </Form.Item>
          <Form.Item
            name="publishStrategy"
            label="发布策略"
            rules={[{ required: true, message: '请选择发布策略' }]}
            initialValue="auto"
          >
            <Radio.Group>
              <Radio value="auto">自动发布</Radio>
              <Radio value="manual">手动发布</Radio>
              <Radio value="scheduled">定时发布</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="publishSchedule" label="发布计划">
            <Input placeholder="如：09:00,14:00,20:00" />
          </Form.Item>
          <Form.Item
            name="dailyPublishLimit"
            label="每日发布限制"
            rules={[{ required: true, message: '请输入每日发布限制' }]}
            initialValue={10}
          >
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="dailyPrivateChatCount"
            label="每日私聊限制"
            rules={[{ required: true, message: '请输入每日私聊限制' }]}
            initialValue={50}
          >
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="persona"
            label="账号人设"
            rules={[{ required: true, message: '请输入账号人设' }]}
            initialValue="纯评论"
          >
            <Select placeholder="请选择账号人设">
              <Option value="纯评论">纯评论</Option>
              <Option value="美女主播">美女主播</Option>
              <Option value="体育专业分析">体育专业分析</Option>
              <Option value="游戏攻略">游戏攻略</Option>
              <Option value="生活分享">生活分享</Option>
              <Option value="搞笑娱乐">搞笑娱乐</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="账号描述" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlatformAccountConfig;

